<template>
  <div>
    <FormBox
      :loading="isLoading"
      :disabled="!claims.billing['ROLE_POST_CREATE_OR_UPDATE_DISCOUNT'] || disabled"
      :title="$t(`${namespace}.incentives`)"
      :icon="require('@/assets/icons/icn_medal-money-accent.svg')"
      :subheaderTitle="$t(`${namespace}.incentiveSubtitle`)"
      :subheaderDesc="$t(`${namespace}.incentiveDesc`)"
      toggle
      :edit="claims.billing['ROLE_POST_CREATE_OR_UPDATE_DISCOUNT'] && hasDiscountApplyed"
      :visible="incentiveContainer"
      @toggleVisibility="toggleVisibility"
      @edit="openConfirmEditDialog"
    >
      <div style="min-height: 94px;">
        <div v-if="isSuccess">
          <form
            v-if="action === 'edit'"
            class="form--custom"
            novalidate
            @submit.prevent="handleSubmit"
          >
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <div class="pr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldSaasByRideValueLabel`) }}</label>
                  <v-text-field
                    v-model="saasByRide.value"
                    type="number"
                    :placeholder="$t(`${namespace}.fieldSaasByRideValuePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldSaasByRideValueName`))"
                    v-validate="saasByRide.expirationDate ? {
                      required: true,
                      integer: true,
                      min_value: 1
                    } : {}"
                    :data-vv-name="$t(`${namespace}.fieldSaasByRideValueName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>

              <v-flex sm6 xs12>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldSaasByRideExpirationDateLabel`) }}</label>
                  <v-text-field
                    v-model="saasByRide.expirationDate"
                    type="number"
                    :placeholder="$t(`${namespace}.fieldSaasByRideExpirationDatePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldSaasByRideExpirationDateName`))"
                    v-validate="saasByRide.value ? {
                      required: true,
                      integer: true,
                      min_value: 1
                    } : {}"
                    :data-vv-name="$t(`${namespace}.fieldSaasByRideExpirationDateName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex sm6 xs12>
                <div class="pr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldPercentValueLabel`) }}</label>
                  <v-text-field
                    v-model="percent.value"
                    type="number"
                    :placeholder="$t(`${namespace}.fieldPercentValuePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldPercentValueName`))"
                    v-validate="percent.expirationDate ? {
                      required: true,
                      integer: true,
                      min_value: 1,
                      max_value: 100
                    } : {}"
                    :data-vv-name="$t(`${namespace}.fieldPercentValueName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>

              <v-flex sm6 xs12>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldPercentExpirationDateLabel`) }}</label>
                  <v-text-field
                    v-model="percent.expirationDate"
                    type="number"
                    :placeholder="$t(`${namespace}.fieldPercentExpirationDatePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldPercentExpirationDateName`))"
                    v-validate="percent.value ? {
                      required: true,
                      integer: true,
                      min_value: 1
                    } : {}"
                    :data-vv-name="$t(`${namespace}.fieldPercentExpirationDateName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>
            </v-layout>

            <div class="text-xs-right">
              <a
                v-if="!isFetching && enableCancelLink"
                href="#"
                :title="$t('Common.cancel')"
                class="accent--text mr-2"
                @click.prevent="cancel"
              >{{ $t('Common.cancel') }}</a>

              <v-btn
                class="mr-0"
                :disabled="isFetching || disabledSubmitButton"
                :loading="isFetching"
                type="submit"
                color="accent"
                depressed
                round
              >
                {{ $t('Common.save') }}
              </v-btn>
            </div>
          </form>

          <div v-if="action === 'preview'">
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <div>
                  <div>{{ $t(`${namespace}.invoiceRides`) }}</div>
                  <div class="caption gray-6--text">
                    <span v-if="form.saasByRide.value">
                      {{ form.saasByRide.value }} {{ $t(`${namespace}.rides`) }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </v-flex>

              <v-flex sm6 xs12>
                <div>
                  <div>{{ $t(`${namespace}.invoiceRidesPeriod`) }}</div>
                  <div class="caption gray-6--text">
                    <span v-if="form.saasByRide.startDate && form.saasByRide.expirationDate">
                      {{ $t(`${namespace}.from`) }} {{ form.saasByRide.startDate | formatDate('DD/MM/YYYY') }} {{ $t(`${namespace}.to`) }} {{ form.saasByRide.expirationDate | formatDate('DD/MM/YYYY') }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex sm6 xs12>
                <div class="mt-3">
                  <div>{{ $t(`${namespace}.rebate`) }}</div>
                  <div class="caption gray-6--text">
                    <span v-if="form.percent.value">
                      {{ form.percent.value }}%
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </v-flex>

              <v-flex sm6 xs12>
                <div class="mt-3">
                  <div>{{ $t(`${namespace}.rebatePeriod`) }}</div>
                  <div class="caption gray-6--text">
                    <span v-if="form.percent.startDate && form.percent.expirationDate">
                      {{ $t(`${namespace}.from`) }} {{ form.percent.startDate | formatDate('DD/MM/YYYY') }} {{ $t(`${namespace}.to`) }} {{ form.percent.expirationDate | formatDate('DD/MM/YYYY') }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <v-layout
          v-if="isFailed"
          class="text-xs-center min-height"
          align-center
          justify-center
          row
          fill-height
        >
          <v-flex class="text-xs-center">
            <span>
              {{ $t('Common.genericErrorMsg') }}<br>
              <a
                href="#"
                :title="$t('Common.tryAgain')"
                class="text--link accent--text"
                @click.prevent="loadData"
              >
                {{ $t('Common.tryAgain') }}
              </a>
            </span>
          </v-flex>
        </v-layout>
      </div>
    </FormBox>

    <ConfirmEditDialog
      :visible="confirmEditDialog"
      @cancel="closeConfirmEditDialog()"
      @close="closeConfirmEditDialog('edit')"
    />

    <DisableIncentivesDialog
      :visible="disableIncentivesDialog"
      :saasByRideDiscountId="saasByRideDiscountId"
      :percentDiscountId="percentDiscountId"
      @cancel="cancelDisableIncentives"
      @close="resetIncentiveFields"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import { isArray, isObject } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import FormBox from 'components/FormBox'
import ConfirmEditDialog from './ConfirmEditDialog'
import DisableIncentivesDialog from './DisableIncentivesDialog'

export default {
  name: 'Incentives',
  components: {
    FormBox,
    ConfirmEditDialog,
    DisableIncentivesDialog
  },
  computed: {
    ...mapState({
      claims: state => state.userClaims.claims,
      company: ({ company }) => company.selectedCompany
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    hasDiscountApplyed () {
      const { saasByRide = {}, percent = {} } = this.form
      return Boolean(saasByRide.value && saasByRide.expirationDate) || Boolean(percent.value && percent.expirationDate)
    },

    disabledSubmitButton () {
      const { saasByRide, percent } = this
      return !(Boolean(saasByRide.value) || Boolean(saasByRide.expirationDate) || Boolean(percent.value) || Boolean(percent.expirationDate))
    },

    enableCancelLink () {
      const { saasByRide, percent } = this.form
      return Boolean(saasByRide.id) || Boolean(percent.id)
    }
  },
  data () {
    return {
      namespace: 'Components.Incentives',
      incentiveContainer: false,
      disabled: false,
      currentStatus: null,
      isFetching: false,
      action: 'preview',
      firstLoading: true,

      // dialogs
      confirmEditDialog: false,
      disableIncentivesDialog: false,
      saasByRideDiscountId: null,
      percentDiscountId: null,

      // form
      saasByRide: {
        value: null,
        expirationDate: null
      },
      percent: {
        value: null,
        expirationDate: null
      },
      form: {
        saasByRide: {
          value: null,
          expirationDate: null,
          reason: 'DEFAULT REASON',
          unit: 'SAAS_BY_RIDE'
        },
        percent: {
          value: null,
          expirationDate: null,
          reason: 'DEFAULT REASON',
          unit: 'PERCENT'
        }
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    isValidObject (val) {
      return isObject(val)
    },

    openConfirmEditDialog () {
      if (this.action === 'preview') {
        this.confirmEditDialog = true
      }
    },

    closeConfirmEditDialog (action = null) {
      this.confirmEditDialog = false
      action && this.toogleAction(action)
    },

    openDisableIncentivesDialog () {
      if (this.incentiveContainer) {
        this.disableIncentivesDialog = true
      }
    },

    closeDisableIncentivesDialog (val) {
      this.disableIncentivesDialog = false
    },

    cancelDisableIncentives () {
      this.closeDisableIncentivesDialog()
      this.disabled = false
    },

    toogleAction (action = 'preview') {
      this.action = action
    },

    resetForm () {
      this.saasByRide = {
        value: null,
        expirationDate: null
      }

      this.percent = {
        value: null,
        expirationDate: null
      }

      this.disabled = false
    },

    async resetIncentiveFields () {
      this.incentiveContainer = false

      this.saasByRide = {
        value: null,
        expirationDate: null
      }

      this.percent = {
        value: null,
        expirationDate: null
      }

      this.form = {
        saasByRide: {
          value: null,
          expirationDate: null,
          reason: 'DEFAULT REASON',
          unit: 'SAAS_BY_RIDE'
        },
        percent: {
          value: null,
          expirationDate: null,
          reason: 'DEFAULT REASON',
          unit: 'PERCENT'
        }
      }

      setTimeout(() => { this.closeDisableIncentivesDialog() }, 400)
      await this.loadData()
      this.disabled = false
    },

    toggleVisibility (val) {
      if (!this.firstLoading && !val) {
        // this.toogleAction(this.action)
        this.openDisableIncentivesDialog()
        // this.incentiveContainer = true
        if (this.hasDiscountApplyed) {
          setTimeout(() => {
            document.querySelector('[type="checkbox"]').click()
            this.disabled = true
          }, 200)
        }
      }
    },

    integerToExpirationDate (val) {
      if (!val) return
      const months = Number(val)
      const expirationDate = `${moment().add(months, 'months').format('YYYY-MM-DDTHH:mm:ssZ')}`
      return expirationDate
    },

    cancel () {
      this.toogleAction('preview')
    },

    setPayload (data = null) {
      function getSaasByRide (arr) {
        const match = arr.find(item => item.unit === 'SAAS_BY_RIDE') || {}
        return match
      }

      function getPercent (arr) {
        const match = arr.find(item => item.unit === 'PERCENT') || {}
        return match
      }

      const { saasByRide, percent } = this.form
      const saasByRideData = getSaasByRide(data)
      const percentData = getPercent(data)

      this.saasByRideDiscountId = this.isValidObject(saasByRideData) && saasByRideData.id
        ? saasByRideData.id
        : null

      this.percentDiscountId = this.isValidObject(percentData) && percentData.id
        ? percentData.id
        : null

      if (data) {
        this.form = {
          saasByRide: {
            ...saasByRide,
            ...saasByRideData
          },
          percent: {
            ...percent,
            ...percentData
          }
        }
      }
    },

    setBoxBehavior (action = null) {
      const toogleAction = action || (this.hasDiscountApplyed ? 'preview' : 'edit')
      this.toogleAction(toogleAction)
      this.incentiveContainer = this.hasDiscountApplyed
    },

    async loadData (action) {
      try {
        const companyId = this.company && this.company.id ? this.company.id : null

        if (companyId) {
          this.currentStatus = STATUS_IS_LOADING
          const { data: res } = await this.$apiService.getDiscounts(companyId)

          // has discount applyed
          if (res && res.content && this.isValidArray(res.content)) {
            this.setPayload(res.content)
          }

          this.setBoxBehavior(action)
          this.currentStatus = STATUS_SUCCESS
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    renderPayload () {
      const { saasByRide = {}, percent = {} } = this.form
      const saasByRideValue = this.saasByRide.value ? Number(this.saasByRide.value) : null
      const percentvalue = this.percent.value ? Number(this.percent.value) : null
      const incentives = [
        { ...saasByRide, value: saasByRideValue },
        { ...percent, value: percentvalue }
      ]
      const incentivesFiltered = incentives.filter(incentives => incentives.value && incentives.expirationDate)
      return incentivesFiltered
    },

    async handleSubmit () {
      this.isFetching = true

      try {
        const isValid = await this.$validator.validateAll()

        if (isValid) {
          const companyId = this.company && this.company.id ? this.company.id : null
          const { data: res } = await this.$apiService.setDiscount(companyId, this.renderPayload())
          if (res && !res.valid) {
            throw new Error()
          }
          this.resetForm()
          await this.loadData('preview')

          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.editSuccess`)
          })
        }
      } catch (err) {
        console.log('ERROR', err)

        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.editError`)
        })
      }

      this.isFetching = false
    }
  },
  watch: {
    'saasByRide.expirationDate' (val) {
      this.form.saasByRide.expirationDate = this.integerToExpirationDate(val)
    },

    'percent.expirationDate' (val) {
      this.form.percent.expirationDate = this.integerToExpirationDate(val)
    }
  },
  mounted () {
    this.loadData()
    setTimeout(() => { this.firstLoading = false }, 2000)
  }
}
</script>
